/**
 * O acervo de figuras de torque.
 *
 * São **41 figuras servindo 395 torques**: a mesma sequência de aperto de cabeçote vale
 * para muitos motores, e por isso a figura é cadastrada uma vez e associada. Associar é
 * na ficha do torque; aqui é o acervo em si — ver o que existe e tirar o que não serve.
 *
 * **Cadastrar figura nova existe desde 25/09/2026**, e por rota própria: a antiga
 * (`POST banco-imagens`) recebe só o NOME do arquivo em JSON, o que gravaria uma linha
 * apontando para um arquivo que ninguém enviou. A nova (`banco-imagens/arquivo`) recebe
 * o binário, guarda em `/dados/conteudo/torques` e cadastra numa operação só.
 *
 * A remoção apaga o arquivo pela ApiConteudo, e não só a linha — daí a confirmação em dois
 * passos. **O que a tela ainda não diz é quantos torques usam a figura**, porque a API não
 * conta isso em rota nenhuma; escrevi aqui em vez de deixar a frase sugerindo que diz.
 */

import { useState } from "react"
import {
  type FiguraDoAcervo, usarAcervoDeTorques, usarRemoverDoAcervo, usarSubirFiguraParaAcervo,
} from "@/api/outrosProjetos"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { ErroDaApi } from "@/api/erros"
import { Botao, Painel, Secao, Vazio, estiloCampo, mensagem } from "./pecas"

export function AcervoDeTorques() {
  const acervo = usarAcervoDeTorques()
  const remover = usarRemoverDoAcervo()
  const subir = usarSubirFiguraParaAcervo()
  const [titulo, setTitulo] = useState("")
  const [busca, setBusca] = useState("")
  const [confirmar, setConfirmar] = useState<number | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const todas: FiguraDoAcervo[] = acervo.data ?? []
  const termo = busca.trim().toLowerCase()
  const figuras = termo
    ? todas.filter((f) => `${f.titulo ?? ""} ${f.imagem ?? ""}`.toLowerCase().includes(termo))
    : todas

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Acervo de figuras de torque" detalhe={`${todas.length} figuras`}>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Filtrar por título ou arquivo…"
          style={{ ...estiloCampo, width: 250 }}
        />
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="O acervo é compartilhado por todos os torques" />

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Painel>
          <Secao titulo="As figuras" contagem={`${figuras.length}`}>
            <p style={{ margin: "0 0 9px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5,
                        maxWidth: "78ch" }}>
              Para pôr uma destas num torque, abra o torque e use <b>Associar do acervo</b>. O que
              entra aqui fica disponível para todos os torques.
            </p>

            {/* Cadastrar entra ANTES da lista: numa tela de 41 linhas, o botão embaixo fica
                fora do campo de visão justamente de quem chegou para cadastrar. */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              padding: "10px 11px", marginBottom: 12, borderRadius: 9,
              background: "var(--surface2)", border: "1px solid var(--line2)",
            }}>
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Título da figura (ex.: A15-0063)"
                style={{ ...estiloCampo, flex: "1 1 220px" }}
              />
              <label style={{ display: "inline-flex", alignItems: "center", gap: 7,
                              fontSize: 11.5, cursor: "pointer" }}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.svg,.gif"
                  style={{ fontSize: 11 }}
                  onChange={(e) => {
                    const arquivo = e.target.files?.[0]
                    if (!arquivo) return
                    setRecado(null)
                    subir.mutate({ arquivo, titulo }, {
                      onSuccess: (r) => {
                        setRecado(`Figura ${r.bancoImagemId} cadastrada no acervo.`)
                        setTitulo("")
                      },
                      onError: (x) => setRecado(mensagem(x)),
                    })
                    e.target.value = ""
                  }}
                />
                {subir.isPending && <span style={{ color: "var(--txt2)" }}>enviando…</span>}
              </label>
              <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                jpg, png, svg ou gif — a API recusa o resto
              </span>
            </div>

            {acervo.isPending ? (
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
            ) : acervo.error ? (
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--erro)" }}>
                {acervo.error instanceof ErroDaApi
                  ? acervo.error.message : "Não foi possível ler o acervo."}
              </p>
            ) : figuras.length === 0 ? (
              <Vazio>
                {termo ? "Nenhuma figura com esse texto." : "O acervo está vazio."}
              </Vazio>
            ) : (
              <div style={{ border: "1px solid var(--line2)", borderRadius: 9, overflow: "hidden" }}>
                {figuras.map((f, i) => (
                  <div
                    key={f.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "8px 11px",
                      fontSize: 12, background: "var(--surface)",
                      borderTop: i === 0 ? undefined : "1px solid var(--line)",
                    }}
                  >
                    <span className="num" style={{ flex: "0 0 52px", fontSize: 11,
                                                    color: "var(--txt3)" }}>
                      {f.id}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {f.titulo?.trim() || <i style={{ color: "var(--txt3)" }}>sem título</i>}
                      <span style={{ display: "block", fontSize: 10.5, color: "var(--txt3)" }}>
                        {f.imagem || "sem arquivo"}
                      </span>
                    </span>

                    {confirmar === f.id ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 11, color: "var(--txt2)" }}>
                          apaga o arquivo também —
                        </span>
                        <Botao
                          ocupado={remover.isPending}
                          aoClicar={() => {
                            setRecado(null)
                            remover.mutate(f.id, {
                              onSuccess: () => {
                                setRecado(`Figura ${f.id} removida do acervo.`)
                                setConfirmar(null)
                              },
                              onError: (e) => { setRecado(mensagem(e)); setConfirmar(null) },
                            })
                          }}
                        >
                          confirmar
                        </Botao>
                        <Botao aoClicar={() => setConfirmar(null)}>não</Botao>
                      </span>
                    ) : (
                      <Botao aoClicar={() => { setRecado(null); setConfirmar(f.id) }}>remover</Botao>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Secao>
        </Painel>
      </div>

      <BarraDeEstado direita="41 figuras servem 395 torques">{recado ?? ""}</BarraDeEstado>
    </div>
  )
}
